<template>
  <dx-data-grid
    ref="dataGridRef"
    :allow-column-reordering="true"
    :column-chooser="{ enabled: true, mode: 'select' }"
    :column-min-width="50"
    :columns="columns"
    :data-source="dataSource"
    :filter-panel="{ visible: true }"
    :filter-row="{ visible: true }"
    :header-filter="{ visible: true }"
    :height="50"
    :hover-state-enabled="true"
    :remote-operations="{ filtering: true, paging: true, sorting: true, summary: true }"
    :repaint-changes-only="true"
    :row-alternation-enabled="true"
    :show-borders="true"
    :show-column-lines="true"
    :width="'100%'"
    :word-wrap-enabled="true"
    style="flex-grow: 1"
    value-expr="id"
    @toolbar-preparing="onToolbarPreparing($event)"
    @on-cell-prepared="onCellPrepared($event)"
    @content-ready="onContentReady($event)"
  >
    <dx-paging :enabled="true" :page-size="40" />
    <dx-load-panel :enabled="true" />
    <dx-scrolling mode="virtual" />
    <dx-sorting mode="single" />
    <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
    <dx-editing
      :allow-updating="isEditable"
      :allow-deleting="isEditable"
      :refresh-mode="'full'"
      mode="batch"
    />
    <dx-selection
      mode="single"
    />

    <template #filterTemplatePeriod>
      <mped-calculation-period-component
        :default-period="'current'"
        :default-value="defaultValue"
        @value-changed="onPeriodChanged($event)"
      />
    </template>

    <template #filterMainAsset>
      <div v-if="currentMainAsset" class="d-flex">
        <div><span>Редактирование ОС: </span> <span class="font-bold">{{ currentMainAssetText }}</span></div>
        <div class="dx-show-clear-button ml-1.5" style="cursor: pointer" @click="clearCurrentMainAsset($event)">
          <span class="dx-icon dx-icon-clear"></span>
        </div>
      </div>
    </template>

    <template #editableCellTemplate="{ data }">
      <div class="d-flex">
        <div class="flex-grow-1">{{ data.value }}</div>
        <i class="dx-icon-edit text-success text-opacity-50"></i>
      </div>
    </template>

  </dx-data-grid>

  <footer-grid :show-selected-counter="false" :totalCount="totalCount" class="px-2"></footer-grid>

  <mped-popup-grid-view
    ref="mainAssetChooser"
    :fn-get-data="getMainAsset"
    :columns="columnsMainAsset"
    :choose-item="true"
    popupTitle="Справочник основных средств"
    @selected-item="selectedMainAsset($event)"
  />

</template>

<script>
export default {
  name: 'mped-amortization-move-main-asset-view',
};
</script>

<script setup>
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  DxDataGrid,
  DxScrolling,
  DxLoadPanel,
  DxSorting,
  DxExport,
  DxPaging,
  DxEditing,
  DxSelection
} from 'devextreme-vue/data-grid';

import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import MpedCalculationPeriodComponent
  from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import { addNewRowData, createFilterArray, getAiDataSource } from '@/components/utils/ai-data-source-generator';
import { getMoveMainAsset, setMoveMainAsset } from '@/views/mped/services/mped-amortization-api-service';
import { columnsMoveMainAsset } from '@/views/mped/services/mped-amortization-service';
import { checkHasEditData, getAiToolbar, undoEditData } from '@/components/utils/ai-toolbar-generator';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { columnsAmortizationMainAsset, getPeriods } from '@/views/mped/services/mped-calculation-service';
import {
  getLocalUserdata,
  hasEditablePermissionByUIRoute,
  initState,
  setLocalUserdata,
} from '@/service/local-userdata-service';
import MpedPopupGridView from '@/views/mped/components/mped-popup-grid-view/mped-popup-grid-view.vue';
import {
    getMainAsset,
} from '@/views/mped/services/mped-amortization-api-service';

const totalCount = ref(0);
const periodsValue = ref(null);
const dataGridRef = ref();
const mainAssetChooser = ref();
const currentMainAsset = ref(null);
const router = useRouter();
const columnsMainAsset = columnsAmortizationMainAsset;
const isEditable = hasEditablePermissionByUIRoute(router);

const columns = columnsMoveMainAsset({
  causeList: {
    valueExpr: 'id',
    displayExpr: 'name',
    popupWidth: '250px',
    dataSource: [
      { id: 1, name: 'Начальное сальдо' },
      { id: 2, name: 'Поступление за период' },
      { id: 3, name: 'Выбытие за период' },
      { id: 4, name: 'Списание' }],
  }, periodList: {
    valueExpr: 'id',
    displayExpr: 'id',
    popupWidth: '120px',
    dataSource: getPeriods(10, 5).list,
  },
});

let defaultValue;

const dataSource = getAiDataSource(
  {
    dataFn: getMoveMainAsset,
    key: 'id',
    remoteOperation: true,
    validationFn: () => {
      return periodsValue.value;
    },
    customFilterFn: () => {
      if (currentMainAsset.value) {
        return [['main_asset_id', '=', currentMainAsset.value.id]];
      }

      if (!periodsValue.value.length) return null;

      const periodFilter = createFilterArray(periodsValue.value.reduce((total, item) => {
        total.push(['period', '=', item]);
        return total;
      }, []), 'or');

      return createFilterArray([periodFilter]);
    },
    onLoadedFn: (data) => {
      totalCount.value = data.totalCount;
    },
    insertFn: (values) => {
      return setMoveMainAsset({ insertDataArgs: [values] });
    },
    updateFn: (id, values) => {
      return setMoveMainAsset({ updateDataArgs: [{ id, ...values }] });
    },
    removeFn: (id) => {
      return setMoveMainAsset({ deleteDataArgs: [{ id }] });
    },
    baseSort: [
      { 'selector': 'main_asset_id', 'desc': false },
      { 'selector': 'period', 'desc': false },
      { 'selector': 'date_operation', 'desc': false },
      { 'selector': 'cause_id', 'desc': false }],
  },
);

let toolbarButtons = {}, needAddNewMainAsset = false;

const currentMainAssetText = computed({
  get: () => currentMainAsset.value && `${currentMainAsset.value.main_asset}/${currentMainAsset.value.inventory_number}/${currentMainAsset.value.subnumber}` || '',
});

onBeforeMount(() => {
  const state = getLocalUserdata(router);
  currentMainAsset.value = state?.filters?.currentMainAsset;
  defaultValue = state?.filters?.periods;
  if (defaultValue?.length === 0)
    periodsValue.value = [];
})

onMounted(async () => {
  initState(router, dataGridRef);
});

const reloadData = () => {
  dataSource.reload();
};

const onPeriodChanged = (e) => {
  periodsValue.value = e;
  reloadData();
};

const onToolbarPreparing = (e) => {

  const filters = [{
    location: 'before',
    template: 'filterTemplatePeriod',
  }, {
    location: 'before',
    template: 'filterMainAsset',
  }];

  toolbarButtons = getAiToolbar(e, {
    customs: filters,
    refresh: {
      event: reloadData,
    },
    add: {
      disabled: !isEditable,
      event: addMainAsset
    },
    undo: {
      disabled: !isEditable,
      event: (e) => {
        undoEditData(dataGridRef.value.instance);
      },
    },
    save: {
      disabled: !isEditable,
      event: (e) => {
        dataGridRef.value.instance.saveEditData();
      },
    },
  });
};

const onCellPrepared = (e) => {
  if (e.rowType !== 'data' || e.columnIndex != 0) {
    return;
  }
};

const onContentReady = (e) => {
  checkHasEditData(dataGridRef.value.instance, toolbarButtons);
  if (needAddNewMainAsset) {
    addNewMainAssetRow(currentMainAsset.value);
    needAddNewMainAsset = false;
  }

  setLocalUserdata(router, dataGridRef, {
    periods: periodsValue.value || [],
    currentMainAsset: currentMainAsset.value
  });
};

const addMainAsset = () => {
  if (!currentMainAsset.value)
    mainAssetChooser.value.Open();
  else
    addNewMainAssetRow(currentMainAsset.value);
};

const selectedMainAsset = (e, reload = true) => {
  currentMainAsset.value = e;
  needAddNewMainAsset = true;
  if (reload)
    dataSource.reload();
};

const addNewMainAssetRow = (e) => {
  addNewRowData({
    dxInstance: dataGridRef.value.instance,
    sourceData: {
      main_asset_id: e.id,
      ...e,
    },
    fields: ['department_id', 'department', 'main_asset_id', 'group_amortization', 'main_asset', 'inventory_number', 'name', 'subnumber'],
  });
};

const clearCurrentMainAsset = async () => {
  if (dataGridRef.value.instance.hasEditData()) {
    const result = await AiCustomConfirmDialog(
      'В таблице имеются несохраненные данные! Сохраните или отмените изменения!',
      'Информация',
      false,
      false,
    );
    return;
  }
  currentMainAsset.value = null;
  dataSource.reload();
};

</script>

<style scoped>

</style>
